import React, {
  useState,
  useCallback,
  useEffect,
  ComponentType,
  ReactNode,
} from 'react';

import {
  fetchWithTokenAndRegion,
  getBim360AdminUrl,
  getCurrentToken,
  getCurrentTokenWithoutRedirect,
  getOxygenId,
} from '@adsk/acs-sc-web-platform-api';
import { useSWRAlloy, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { parseUser } from './helpers/parseUser';
import { User, UserDTO } from './types';

type UserContextValue = {
  data: null | User;
  error?: null | Error;
  loading: boolean;
  retry(): void;
  setUser(data: UserContextValue['data']): void;
  setEnabled?: (enabled: boolean) => void;
};

type UserProviderProps = {
  redirectLoggedOutUsers?: boolean;
  disabled?: boolean;
  children?: ReactNode;
};

/**
 * Context for current user.
 */
const UserContext = getOrCreateGlobalContext<UserContextValue>(
  CONTEXT_NAMES.UserContext,
);

const useUserContext = () =>
  useContextWithValidation(UserContext, {
    hook: 'useUser',
    provider: 'UserProvider',
  });

export const useOptionalUser = () => {
  const { setEnabled, ...rest } = useUserContext();
  useEffect(() => {
    if (setEnabled) {
      setEnabled(true);
    }
  }, [setEnabled]);
  return rest;
};

const fetchUser = () =>
  getCurrentToken().then(({ accessToken }) => {
    const oxygenId = getOxygenId(accessToken);
    return fetchWithTokenAndRegion<UserDTO>(
      `${getBim360AdminUrl()}/v1/users/${oxygenId}`,
    ).then(parseUser);
  });

export const UserProvider: ComponentType<UserProviderProps> = ({
  children,
  redirectLoggedOutUsers = true,
  disabled,
}) => {
  const [enabled, setEnabled] = useState(!disabled);
  const {
    data: fetchData,
    error,
    revalidate: retry,
    mutate,
  } = useSWRAlloy(enabled ? '/current_user' : null, () => {
    const fetchUserFn =
      redirectLoggedOutUsers || !enabled
        ? fetchUser
        : () => getCurrentTokenWithoutRedirect().then(() => fetchUser());
    return fetchUserFn().catch((e: unknown) => {
      if (redirectLoggedOutUsers) {
        console.error(
          'AlloyProvider - UserContext: Error fetching current user',
          e,
        );
      }
      throw e;
    });
  });
  const data = error?.status === 401 ? null : fetchData;
  const loading = !data && !error;

  const setUser = useCallback(
    (newData: unknown) => mutate(newData, false),
    [mutate],
  );

  return (
    <UserContext.Provider
      value={{
        error,
        loading,
        data,
        retry,
        setUser,
        setEnabled: enabled ? undefined : setEnabled,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.displayName = 'UserProvider';
